import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Badge,
} from "@mui/material";
import { Close as CloseIcon, StickyNote2Outlined } from "@mui/icons-material";
import { baseAxios } from "../../utils/config";
import { toast } from "react-hot-toast";
import NoteDialog from "./NoteDialog";

const statusLabel = (s, isWalkIn) => {
  if (isWalkIn) return "Walk-in";
  return s === 2
    ? "Completed"
    : s === 1
    ? "In Progress"
    : s === 3
    ? "Cancelled"
    : "Scheduled";
};

const statusColor = (s, isWalkIn) =>
  isWalkIn ? "info" : s === 2 ? "success" : s === 1 ? "warning" : s === 3 ? "error" : "default";

function formatAmount(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return value ?? "—";
  return `$${n.toFixed(2)}`;
}

export default function HistoryDialog({ open, onClose, client }) {
  const [appointments, setAppointments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteAppointmentId, setNoteAppointmentId] = useState(null);

  const loadHistory = () => {
    if (!client?.client_id) return;
    setLoading(true);
    Promise.all([
      baseAxios.get(`/scheduler/pastAppointments/${client.client_id}`),
      baseAxios.get(`/clients/${client.client_id}/notes`),
    ])
      .then(([appointmentsRes, notesRes]) => {
        if (appointmentsRes.status === 200) {
          setAppointments(appointmentsRes.data || []);
        }
        if (notesRes.status === 200) {
          setNotes(notesRes.data?.notes || []);
        }
      })
      .catch(() =>
        toast.error("Error fetching past appointments", { duration: 3000 })
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !client?.client_id) {
      setAppointments([]);
      setNotes([]);
      setNoteDialogOpen(false);
      setNoteAppointmentId(null);
      return;
    }
    loadHistory();
  }, [open, client?.client_id]);

  const totalSpent = appointments.reduce(
    (sum, apt) => sum + Number(apt.totalAmount || 0),
    0
  );

  const contactLine = [client?.client_mobile_phone_number, client?.client_email]
    .filter(Boolean)
    .join("  ·  ");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          maxHeight: "min(88vh, 720px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 2.5,
          py: 1.25,
          bgcolor: "secondary.main",
          color: "common.white",
          borderBottom: "1px solid",
          borderColor: "primary.main",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1.5}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                flexWrap: "wrap",
                columnGap: 1,
                rowGap: 0.25,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="inherit"
                sx={{ lineHeight: 1.3 }}
              >
                Appointment history
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.75)",
                  lineHeight: 1.3,
                }}
                noWrap
              >
                {[client?.client_first_name, client?.client_last_name]
                  .filter(Boolean)
                  .join(" ")}
                {contactLine ? ` · ${contactLine}` : ""}
              </Typography>
            </Box>
            {client && !loading && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "primary.main",
                  mt: 0.5,
                  fontWeight: 600,
                  letterSpacing: 0.2,
                }}
              >
                {appointments.length} visit
                {appointments.length === 1 ? "" : "s"}
                {" · "}
                Total {formatAmount(totalSpent)}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{
              color: "primary.main",
              flexShrink: 0,
              p: 0.5,
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              py: 8,
              px: 3,
            }}
          >
            <CircularProgress size={28} color="primary" />
            <Typography variant="body2" color="text.secondary">
              Loading appointment history…
            </Typography>
          </Box>
        ) : appointments.length > 0 ? (
          <Box sx={{ overflow: "auto", maxHeight: "min(56vh, 480px)" }}>
            {appointments.map((apt, i) => {
              const services =
                apt.services?.map((s) => s.serviceName).filter(Boolean) || [];
              const stylist = [apt.stylistName, apt.stylistLastName]
                .filter(Boolean)
                .join(" ");

              return (
                <Box
                  key={apt.appointmentId ?? `walk-in-${apt.receiptId}`}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderBottom:
                      i < appointments.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                    bgcolor: i % 2 === 0 ? "grey.50" : "background.paper",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 2,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="text.primary"
                    >
                      {new Date(apt.date).toLocaleDateString("en-US", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="success.main"
                      sx={{ flexShrink: 0 }}
                    >
                      {formatAmount(apt.totalAmount)}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ mb: 0.5, lineHeight: 1.4 }}
                  >
                    {services.length > 0 ? services.join(", ") : "No services"}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {stylist && (
                      <Typography variant="caption" color="text.secondary">
                        {stylist}
                      </Typography>
                    )}
                    {stylist && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        aria-hidden
                      >
                        ·
                      </Typography>
                    )}
                    <Chip
                      label={statusLabel(apt.appointmentStatus, apt.isWalkIn)}
                      size="small"
                      color={statusColor(apt.appointmentStatus, apt.isWalkIn)}
                      variant="outlined"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                    <Badge
                      badgeContent={
                        notes.filter(
                          (item) =>
                            apt.appointmentId &&
                            item.schedule_entry_id === apt.appointmentId
                        ).length
                      }
                      color="primary"
                      sx={{ ml: "auto" }}
                    >
                      <IconButton
                        size="small"
                        aria-label="Add note for this appointment"
                        title="Add note"
                        onClick={() => {
                          setNoteAppointmentId(apt.appointmentId ?? null);
                          setNoteDialogOpen(true);
                        }}
                        sx={{ p: 0.25, color: "text.secondary" }}
                      >
                        <StickyNote2Outlined sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Badge>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ py: 8, px: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No appointment history for this client.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <NoteDialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        client={client}
        appointments={appointments}
        initialAppointmentId={noteAppointmentId}
        onSaved={loadHistory}
      />
    </Dialog>
  );
}
